import {
  Box,
  Chip,
  IconButton,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Apps as AppsIcon } from "@mui/icons-material";
import { useHistory, useLocation } from "react-router-dom";
import MetaTags from "react-meta-tags";

import { CATALOG_TITLE, CATALOG_LINK_CANONICAL } from "helpers/metatags";

import { EmptyResults } from "components/catalog/EmptyResults/EmptyResults";
import FilterPanel from "components/catalog/FilterPanel";
import MarketingMessage from "components/catalog/MarketingMessage";
import CatalogTable from "components/catalog/Table";
import { FusionPartnerBox } from "components/FusionPartnerBox";
import Page from "components/wrappers/Page";
import RightContent from "components/wrappers/RightContent";
import SortBy from "components/shared/SortBy";
import useAuth from "hooks/useAuth";
import useCatalog from "hooks/useCatalog";
import { IDesign } from "types/design";
import { ICatalogSearchTerm } from "types/catalog";
import { GeneType, ISort, SortOrder } from "types/common";
import useApp, { ProductLevel } from "../hooks/useApp";

const CatalogSorts: ISort<IDesign>[] = [
  {
    label: "Read Low-High",
    field: "readsRecommended",
    order: SortOrder.ASC,
  },
  {
    label: "Read High-Low",
    field: "readsRecommended",
    order: SortOrder.DESC,
  },
  {
    label: "Genes Low-High",
    field: "numberGenes",
    order: SortOrder.ASC,
  },
  {
    label: "Genes High-Low",
    field: "numberGenes",
    order: SortOrder.DESC,
  },
];

const useStyles = makeStyles((theme: Theme) => ({
  switchIcon: {
    backgroundColor: "white",
    marginRight: 16,
    "& > *": {
      width: 16,
      height: 16,
    },
  },
  filterLabel: {
    letterSpacing: 0.1,
    color: theme.palette.grey[300],
    fontWeight: 400,
    marginRight: 12,
  },
  h1Mobile: {
    // using 500px instead of xs helps with awkward breakpoint between 639-420
    [theme.breakpoints.down(500)]: {
      fontWeight: 600,
      fontSize: 21,
      lineHeight: "auto",
      "& span": { fontSize: 14 },
    },
  },
}));

const Catalog = () => {
  const classes = useStyles();
  const { search } = useLocation();
  const history = useHistory();
  const { user } = useAuth();
  const { productLevel } = useApp();
  const {
    genes,
    isPartnerIncluded,
    sortBy,
    setGenes,
    totalCount,
    setSortBy,
    loading,
    loaded,
  } = useCatalog();

  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const onFilterDelete = () => {
    if (typeof URLSearchParams !== "undefined") {
      const queryParams = new URLSearchParams(search);
      Object.values(GeneType).forEach((type) => {
        queryParams.delete(type);
      });
      history.replace({ search: queryParams.toString() });
    } else {
      setGenes([]);
    }
  };

  return (
    <Page>
      <MetaTags>
        <title>{CATALOG_TITLE}</title>
        <link rel="canonical" href={CATALOG_LINK_CANONICAL} />
      </MetaTags>

      <FilterPanel />
      <RightContent>
        <Box display="flex" flexDirection="column" gap={{ xs: "16px", sm: "24px" }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="h1"
              className={isMobile ? classes.h1Mobile : ""}
            >
              Catalog{" "}
              <Typography
                variant="body1"
                color="textSecondary"
                component="span"
              >
                {totalCount || 0} Results
              </Typography>
            </Typography>
            <Box>
              {productLevel !== ProductLevel.MVP && (
                <IconButton
                  color="inherit"
                  size="small"
                  className={classes.switchIcon}
                >
                  <AppsIcon />
                </IconButton>
              )}
              <SortBy<IDesign>
                sortBy={sortBy}
                setSortBy={setSortBy}
                sorts={CatalogSorts}
              />
            </Box>
          </Box>

          {!!genes.length && (
            <Box display="flex" alignItems="center">
              <Typography
                variant="subtitle2"
                component="span"
                className={classes.filterLabel}
              >
                Filter:
              </Typography>
              {genes.length > 0 && (
                <Chip
                  label={genes
                    .map((gene: ICatalogSearchTerm) => gene.name)
                    .join(" / ")}
                  onDelete={onFilterDelete}
                  clickable
                />
              )}
            </Box>
          )}

          {loaded && !loading && totalCount === 0 && <EmptyResults />}

          {isPartnerIncluded && <FusionPartnerBox />}

          {!Boolean(user) && <MarketingMessage hasElevation />}

          {(!loaded || loading || totalCount > 0) && <CatalogTable />}
        </Box>
      </RightContent>
    </Page>
  );
};

export default Catalog;
